/**
 * Icons (docs/chats.md#1-a-chat): what a chat, a person, an organization, or a view shows for itself, in one shape. An
 * emoji; a picture the server keeps, by its address, `tinted` when it is a one-color mark on a transparent ground that
 * clients draw in the text color; or, for a view alone, an SF Symbol's name. Pictures come by upload; what a client sends
 * as JSON is an emoji, a view's symbol, or null to clear it.
 */
import { attempt } from "./quietly";

export interface EmojiIcon {
  emoji: string;
}
export interface PictureIcon {
  picture: string;
  tinted: boolean;
}
export interface SymbolIcon {
  symbol: string;
}
export type Icon = EmojiIcon | PictureIcon | SymbolIcon;
/** The icons a chat, a person, or an organization may have: a symbol is a view's alone. */
export type OwnIcon = EmojiIcon | PictureIcon;

/** An SF Symbol's name, as the Coast app looks it up. */
export const SYMBOL_NAME = /^[a-z0-9.]{1,60}$/;
/** A view's icon when neither its manifest nor its chat names one. */
export const VIEW_ICON: SymbolIcon = { symbol: "square.stack" };
/** Where the server serves the pictures it keeps, `/api/v1/pictures/{id}`. */
const PICTURE_PATH = /^\/api\/v1\/pictures\/pic_[a-f0-9]{32}$/;

/** One emoji: a single grapheme of at most a few code points, trimmed, or null. */
export function emojiOf(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const emoji = value.trim();
  const graphemes = [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(emoji)];
  return graphemes.length === 1 && emoji.length <= 16 && /\p{Extended_Pictographic}/u.test(emoji) ? emoji : null;
}

/**
 * An icon a client sets as JSON: `{emoji}`, `{symbol}` where `symbols` allows it, or null to clear it; undefined when it
 * is neither, a picture included, since pictures come by upload.
 */
export function iconInput(
  value: unknown,
  options: { symbols?: boolean } = {},
): EmojiIcon | SymbolIcon | null | undefined {
  if (value === null) return null;
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const { emoji, symbol, ...rest } = value as { emoji?: unknown; symbol?: unknown };
  if (Object.keys(rest).length) return undefined;
  if (emoji !== undefined && symbol === undefined) {
    const one = emojiOf(emoji);
    return one ? { emoji: one } : undefined;
  }
  if (symbol !== undefined && emoji === undefined && options.symbols)
    return typeof symbol === "string" && SYMBOL_NAME.test(symbol) ? { symbol } : undefined;
  return undefined;
}

/** Whether `value` is an icon as the server keeps and hands them out, a picture at one of its own addresses included. */
export function isIcon(value: unknown): value is Icon {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const icon = value as Record<string, unknown>;
  const keys = Object.keys(icon).sort().join(",");
  if (keys === "emoji") return emojiOf(icon.emoji) === icon.emoji;
  if (keys === "symbol") return typeof icon.symbol === "string" && SYMBOL_NAME.test(icon.symbol);
  if (keys === "picture,tinted")
    return typeof icon.picture === "string" && PICTURE_PATH.test(icon.picture) && typeof icon.tinted === "boolean";
  return false;
}

/** A chat's, a person's, or an organization's icon read back from storage: never a symbol. */
export function storedOwnIcon(text: string | null): OwnIcon | null {
  const icon = storedIcon(text);
  return icon && !("symbol" in icon) ? icon : null;
}

/** An icon read back from storage, or null when it isn't one, such as a row from before icons took this shape. */
export function storedIcon(text: string | null): Icon | null {
  const value = text ? attempt((): unknown => JSON.parse(text)) : null;
  return isIcon(value) ? value : null;
}

/** What draws an uploaded SVG icon into a PNG (src/server/svg.ts). */
export interface SvgRenderer {
  /** The PNG of `svg`, and whether everything it draws is one color; throws when it can't be read. */
  png(svg: string): Promise<{ png: Uint8Array<ArrayBuffer>; oneColor: boolean }>;
}

/** The SVG's text when `bytes` are an SVG document, by their content, else null. */
export function svgText(bytes: Uint8Array): string | null {
  if (bytes.includes(0)) return null;
  const text = new TextDecoder().decode(bytes).replace(/^﻿/, "");
  // An SVG starts with its root, after any XML declaration, comments, and doctype.
  const root = text.replace(/^\s*(<\?xml[^>]*\?>\s*)?((<!--[\s\S]*?-->|<!DOCTYPE[^>]*>)\s*)*/i, "");
  return /^<svg[\s/>]/i.test(root) ? text : null;
}

/**
 * A chat's icon as the Coast app hands it to the runtime with a page (the bridge's `open`): the icon as the API gives it,
 * or, from a Coast app built before icons took this shape, its emoji as a string.
 */
export function iconFromApp(value: unknown): OwnIcon | null {
  if (typeof value === "string") {
    const emoji = emojiOf(value);
    return emoji ? { emoji } : null;
  }
  return isIcon(value) && !("symbol" in value) ? value : null;
}

/** An icon as a view's page reads it (`coast.chat.icon`, docs/views.md#6-the-page-api): a picture at its full address. */
export function pageIcon(icon: OwnIcon | null | undefined, server: string): OwnIcon | null {
  if (!icon) return null;
  return "picture" in icon ? { picture: `${server}${icon.picture}`, tinted: icon.tinted } : icon;
}

/** Where pictures are served, which a view's page may load them from: its policy's `img-src` names it. */
export const PICTURES_PATH = "/api/v1/pictures/";
/**
 * The largest picture an icon's upload takes, a JPEG, PNG, or SVG; the Coast app sends a 512-pixel square JPEG of a few
 * dozen kilobytes, or a 256-pixel PNG, and `ContractsTests` holds it to this limit.
 */
export const PICTURE_BYTES = 256 * 1024;

/**
 * A view's icon as a chat gives it in place of the view's own: `{emoji}` or `{symbol}` as JSON, or, as a tool's text, one
 * emoji or an SF Symbol's name; null to show the view's own again, undefined when it is none of these.
 */
export function viewIconInput(value: unknown): EmojiIcon | SymbolIcon | null | undefined {
  if (typeof value !== "string") return iconInput(value, { symbols: true });
  const emoji = emojiOf(value);
  if (emoji) return { emoji };
  return SYMBOL_NAME.test(value) ? { symbol: value } : undefined;
}
